import { useEffect, useState } from 'react'
import { AxiosResponse } from 'axios'
import pclnRequest from '@pcln/request'
import config from 'isomorphic-config'
import analytics from '@/shared-utils/analytics'
import { GraphQLResponse, RecommendedCityIds } from '@/types'

type CityInfo = {
  cityId: number
  cityName: string
  stateCode: string
  stateName: string
  countryCode: string
  countryName: string
  itemName: string
  latitude: number
  longitude: number
  lat: number
  lon: number
}

type QueryCityInfoData<CityId extends number = number> = {
  [key in `city_${CityId}`]: CityInfo
}

let cachedRecommendedCityResults: CityInfo[] = []

const generateQuery = (locations: string[]) => {
  return locations.reduce((query, cityId) => {
    return `${query}
    city_${cityId}: cityInfo(location: "${cityId}") {
        cityName
        countryCode
        countryName
        stateCode
        stateName
        latitude
        longitude
        itemName: displayCityName
        lat
        lon
        cityID: cityId
      }`
  }, '')
}

const useRecommendations = (
  shouldFetchCities = false,
  cityIdList?: RecommendedCityIds
) => {
  const [recommendedCities, setRecommendedCities] = useState<CityInfo[]>([])
  useEffect(() => {
    let cancelRequest = false
    async function fetchRecommendations(cityIds: string[]) {
      if (cachedRecommendedCityResults.length > 0) {
        setRecommendedCities(cachedRecommendedCityResults)
        return
      }
      const query = generateQuery(cityIds)
      try {
        const { url, timeout } = config.client['pcln-graph']
        const response: AxiosResponse<GraphQLResponse<QueryCityInfoData>> =
          await pclnRequest({
            url,
            timeout,
            method: 'POST',
            data: {
              query: `query { ${query} }`
            }
          })
        const { errors, data } = response.data
        if (Array.isArray(errors) && errors.length > 0) {
          analytics.logError({
            message: `Failed to hotelRecommendations api. Error message: ${errors[0]?.message}`
          })
          return
        }
        if (data) {
          cachedRecommendedCityResults = Object.values(data)
          if (cancelRequest) return
          setRecommendedCities(cachedRecommendedCityResults)
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : ((error as any).toString() as string)
        analytics.logError({
          message: `Failed to hotelRecommendations api. Error message: ${errorMessage}`
        })
      }
    }
    if (!shouldFetchCities || recommendedCities?.length > 0) {
      return
    }
    if (cityIdList?.cityId.length) {
      fetchRecommendations(cityIdList.cityId.map(x => x.value))
    }

    return function cleanup() {
      cancelRequest = true
    }
  }, [recommendedCities, cityIdList, shouldFetchCities])

  return recommendedCities
}

export default useRecommendations
